/** Draft step AI toolbar — the note from the last run (what it found · check or supply · legal basis), Regenerate / Improve mine
 *  (a background GPT-5.5 run that reads the whole file; the result is reviewed here before it replaces the author's text). */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Paperclip, Scale, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getWitnessAiRun, startWitnessAi } from "@/lib/api";
import { errMsg, fmtWhen } from "./witnessShared";

function Sources({ s }) {
  if (!s) return null;
  return <span data-testid="ws-ai-sources">Read: {s.notes ?? 0} notes · {s.calendar ?? s.events ?? 0} calendar items · {s.emails ?? 0} emails · {s.pdfs_read ?? 0} PDFs · {s.documents ?? 0} documents · {s.repayments ?? 0} repayments{s.airtable ? " · Airtable row" : ""}</span>;
}

function Findings({ res, sources, testPrefix = "ws-ai" }) {
  return (
    <>
      {res.gaps?.length > 0 && (
        <div className="rounded bg-yellow-50 border border-yellow-300 p-1.5" data-testid={`${testPrefix}-gaps`}>
          <div className="font-medium text-amber-900 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Check or supply</div>
          <ul className="list-disc pl-4 text-amber-900">{res.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </div>
      )}
      {res.legal_basis?.length > 0 && (
        <div className="rounded bg-slate-50 border p-1.5" data-testid={`${testPrefix}-law`}>
          <div className="font-medium flex items-center gap-1"><Scale className="w-3 h-3" /> Legal basis relied on</div>
          <ul className="list-disc pl-4 text-slate-700">{res.legal_basis.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </div>
      )}
      {sources && <div className="text-[10px] text-slate-500"><Sources s={sources} /></div>}
    </>
  );
}

/** What the last run found — collapsed to one line once read. */
function LastRunNote({ last }) {
  const [open, setOpen] = useState(true);
  if (!last) return null;
  return (
    <div className="rounded border border-amber-200 bg-white text-[11px]" data-testid="ws-ai-last">
      <button type="button" className="w-full flex items-center gap-1.5 px-2 py-1 text-left" onClick={() => setOpen((o) => !o)} data-testid="ws-ai-last-toggle">
        {open ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
        <Sparkles className="w-3 h-3 text-amber-600" />
        <span className="font-medium">What the AI found</span>
        <span className="text-slate-500 truncate">· {last.mode === "improve" ? "improved" : "generated"} {fmtWhen(last.at)}{last.gaps?.length ? ` · ${last.gaps.length} to check or supply` : " · nothing to supply"}{last.new_exhibits?.length ? ` · exhibits ${last.new_exhibits.join(", ")}` : ""}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          <div data-testid="ws-ai-last-summary">{last.summary}</div>
          <Findings res={last} sources={last.sources} testPrefix="ws-ai-last" />
        </div>
      )}
    </div>
  );
}

export function WitnessAiPanel({ caseId, ws, hasText, onApply, disabled }) {
  const [instructions, setInstructions] = useState(ws.instructions || "");
  const [run, setRun] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const poll = async (runId) => {
    try {
      const r = await getWitnessAiRun(caseId, ws.id, runId);
      setRun(r);
      if (r.status === "running") timer.current = setTimeout(() => poll(runId), 3000);
      else if (r.status === "failed") toast.error(r.error || "The AI run failed");
    } catch (e) { toast.error(errMsg(e)); setRun(null); }
  };
  const start = async (mode) => {
    try {
      const r = await startWitnessAi(caseId, ws.id, { mode, instructions, apply: false });
      setRun(r);
      setOpen(true);
      timer.current = setTimeout(() => poll(r.run_id), 2500);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const running = run?.status === "running";
  const res = run?.status === "done" ? run.result : null;

  return (
    <div className="space-y-1.5" data-testid="ws-ai-panel">
      <LastRunNote last={ws.ai_last} />
      <div className="border rounded-md bg-amber-50/50 border-amber-200">
        <div className="flex items-center gap-2 px-2 py-1.5 flex-wrap">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-[11px] font-medium text-amber-950">GPT-5.5 reads the whole file again</span>
          <span className="flex-1" />
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-amber-900 hover:bg-amber-100" onClick={() => setOpen((o) => !o)} data-testid="ws-ai-toggle">{open ? "Hide" : "Instructions…"}</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300 text-amber-950 hover:bg-amber-100" disabled={disabled || running} onClick={() => start("draft")} title="Write the whole statement again from the file" data-testid="ws-ai-draft">{running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}Regenerate</Button>
          <Button size="sm" className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700" disabled={disabled || running || !hasText} onClick={() => start("improve")} title="Keeps your structure, fixes facts against the file, tightens the law, cites the exhibits you have marked" data-testid="ws-ai-improve">Improve mine</Button>
        </div>
        {open && (
          <div className="px-2 pb-2 space-y-2">
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} className="text-xs bg-white" data-testid="ws-ai-instructions"
              placeholder="Anything the AI should know or do — e.g. 'answer the no-demand point first', 'keep it under 20 paragraphs', 'the balance on 1 Oct was £8,497.83'" />
            {run && (
              <div className="rounded border bg-white p-2 space-y-2 text-[11px]" data-testid="ws-ai-run">
                {running && <div className="flex items-center gap-2 text-amber-900" data-testid="ws-ai-running"><Loader2 className="w-3 h-3 animate-spin" /> {run.stage}…</div>}
                {run.status === "failed" && <div className="text-rose-700 flex items-center gap-1" data-testid="ws-ai-failed"><AlertTriangle className="w-3 h-3" /> {run.error}</div>}
                {res && (
                  <>
                    <div className="flex items-start gap-2">
                      <div className="flex-1" data-testid="ws-ai-summary"><span className="font-medium">{res.paragraphs.length} paragraphs {run.mode === "improve" ? "improved" : "drafted"}.</span> {res.summary}</div>
                      <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => setRun(null)} data-testid="ws-ai-dismiss"><X className="w-3 h-3" /></Button>
                    </div>
                    {res.new_exhibits?.length > 0 && <div className="text-amber-900 flex items-center gap-1" data-testid="ws-ai-new-exhibits"><Paperclip className="w-3 h-3" /> Also marks as exhibits: {res.exhibits.filter((e) => res.new_exhibits.includes(e.label)).map((e) => `${e.label} ${e.title}`).join(" · ")}</div>}
                    {res.heading_fixes && Object.keys(res.heading_fixes).length > 0 && <div className="text-amber-900" data-testid="ws-ai-heading-fixes">Heading from the file: {Object.entries(res.heading_fixes).map(([k, v]) => `${k === "court" ? "court" : "case number"} → ${v}`).join(" · ")}</div>}
                    <Findings res={res} sources={run.sources} />
                    <div className="max-h-40 overflow-y-auto rounded border p-1.5 space-y-1 text-slate-700" data-testid="ws-ai-preview">
                      {res.paragraphs.map((p, i) => <div key={i} className={p.kind === "heading" ? "font-semibold" : ""}><span className="text-slate-400 font-mono mr-1">{p.kind === "heading" ? "§" : i + 1}</span>{p.text}</div>)}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={disabled} onClick={async () => { if (await onApply(run.run_id, true)) setRun(null); }} data-testid="ws-ai-append">Append below mine</Button>
                      <Button size="sm" className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700" disabled={disabled} onClick={async () => { if ((!hasText || window.confirm("Replace every paragraph with this draft? Your current text stays in Versions.")) && await onApply(run.run_id, false)) setRun(null); }} data-testid="ws-ai-replace">Use this draft</Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
